import {
  type AssignedOutletResource,
  type OutletResource,
  OutletsController_list_v1,
  UserAssignmentsController_list_v1,
  UserAssignmentsController_replace_v1,
  type UserAssignmentsController_replace_v1Body,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Checkbox } from '@repo/ui/components/checkbox';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { labelForKind } from '../outlets/outlet-kinds';
import { labelForRole } from '../users/user-roles';
import {
  assignedOutletIds,
  type AssignmentRow,
  mergeAssignments,
  reachesByRole,
} from './assignment-rows';

/**
 * Which outlets a person may work at: `GET`/`PUT /v1/users/{id}/outlets`.
 *
 * **Prefill from `assigned`. Send back `assigned`. `via` is for display.** That
 * one sentence is the whole ticket, and the reason it needs saying is that the
 * obvious implementation gets it wrong in a way nothing catches. Each item of
 * `GET /v1/users/{id}/outlets` carries both fields: `via` answers *may they act
 * here*, and for an owner or a manager it is `ROLE` at **every live outlet**,
 * because FR-111 gives them their reach by role with no row behind it.
 * `assigned` answers *is there actually a row*.
 *
 * For outlet and gate staff the two always agree, which is why a screen built on
 * `via` passes every hand test somebody is likely to run. For owners and managers
 * they do not, and a screen that prefilled its boxes from `via` and `PUT` the
 * ticked set back would send **every live outlet** the first time anybody pressed
 * Save — writing assignment rows that never existed, rostering the park's two
 * most privileged PIN verifiers onto every device in it, and then showing nothing
 * different afterwards, because the `GET` reads identically at zero rows and at
 * ten. The API cannot protect against this: the request is well-formed and means
 * exactly what it says.
 *
 * **Two reads, because one of them is not the universe.** `GET /v1/outlets` is
 * the list of live outlets, and it has to be: for staff the reach list carries
 * only the outlets they already hold, so a table built from it alone could offer
 * what to remove and never what to add. `GET /v1/users/{id}/outlets` supplies
 * `via` and `assigned` on top of it. Both filter to live outlets server-side, so
 * nothing retired can be prefilled and sent straight back.
 *
 * **The other direction is not built here.** `GET /v1/outlets/{id}/users`
 * deliberately carries no `assigned` — owner and manager appear on it `via: ROLE`
 * at every live outlet — and it feeds no write. It is already read by the People
 * screen's outlet filter (FE012), and no `PUT` is built from it anywhere.
 *
 * **Replace-the-set, not add-and-remove.** It is idempotent by nature, and
 * "which outlets does this person work at" is how an owner thinks about it. The
 * server leaves rows already in the set alone rather than re-creating them, so
 * `assigned_at` stays the evidence of when an assignment began.
 */

/** Every state the staffing table can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly rows: readonly AssignmentRow[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The same, for the people who populate the chooser. */
type PeopleState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly people: readonly UserResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 5;

/**
 * How this person reaches an outlet, said in words rather than in the API's.
 *
 * **This column is a judgement call and it is worth knowing which one.** For
 * outlet and gate staff `via` always agrees with the box beside it and the
 * column says nothing the toggle has not already said. It earns its place on the
 * two rows where they disagree — an owner or a manager, where `via` is `ROLE`
 * and `assigned` is `false` — which is the exact case `assignment-rows.ts` warns
 * about in its own header, and which was previously computed on every row and
 * thrown away.
 *
 * An em dash and not "None": the cell is empty because there is nothing there,
 * and a word in a column of two-word phrases reads as a third state.
 */
const labelForVia = (via: AssignmentRow['via']): string => {
  switch (via) {
    case 'ROLE':
      return 'By role';
    case 'ASSIGNMENT':
      return 'By assignment';
    default:
      return '—';
  }
};

export function Staffing() {
  const client = useApiClient();

  const [peopleState, setPeopleState] = useState<PeopleState>({ kind: 'loading' });
  const [personId, setPersonId] = useState<string | null>(null);

  const [state, setState] = useState<ListState>({ kind: 'idle' });

  /**
   * The outlets ticked right now — what Save will send.
   *
   * Seeded from `assigned` and from nothing else, and re-seeded from the
   * response's `assigned` after every successful save.
   */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Counters rather than booleans, so two retries in a row are two distinct
  // values and the second one still fires.
  const [peopleReloads, setPeopleReloads] = useState(0);
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setPeopleState({ kind: 'loading' });

    collectPages<UserResource>((offset, limit) =>
      client.request(UsersController_list_v1, {
        // Active people only, which is the API's default. Somebody deactivated
        // keeps every record they made (FR-106) but is off the roster, and
        // staffing them is a question for the day they come back.
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((people) => {
        if (live) setPeopleState({ kind: 'loaded', people });
      })
      .catch((error: unknown) => {
        if (live) setPeopleState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, peopleReloads]);

  useEffect(() => {
    if (personId === null) {
      setState({ kind: 'idle' });
      return;
    }

    // Two guards, not one. `controller` stops the requests the moment the chooser
    // moves; `live` stops an answer already in flight from being written into
    // state afterwards. Without the second, choosing two people quickly can land
    // the first person's assignments under the second person's name — and this
    // screen writes what it is showing.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });
    setSaveError(null);
    setSaved(false);

    Promise.all([
      collectPages<OutletResource>((offset, limit) =>
        client.request(OutletsController_list_v1, {
          query: { offset, limit },
          signal: controller.signal,
        }),
      ),
      collectPages<AssignedOutletResource>((offset, limit) =>
        client.request(UserAssignmentsController_list_v1, {
          path: { id: personId },
          query: { offset, limit },
          signal: controller.signal,
        }),
      ),
    ])
      .then(([outlets, reach]) => {
        if (!live) return;

        const rows = mergeAssignments(outlets, reach);

        setState({ kind: 'loaded', rows });
        setTicked(assignedOutletIds(reach));
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, personId, reloads]);

  const handleSave = async () => {
    if (personId === null || saving) return;

    setSaveError(null);
    setSaved(false);
    setSaving(true);

    try {
      // The shape comes from the generated client. `ticked` is seeded from
      // `assigned` and only ever changed by somebody clicking a box, so this is
      // the assignment set and not the reach set — sorted so two saves of the
      // same set read as the same request in a log.
      const body: UserAssignmentsController_replace_v1Body = {
        outletIds: [...ticked].sort(),
      };

      // Minted per save and not per mount, which is the one place this screen
      // differs from the dialogs. It is not a dialog: it stays open after a
      // successful save and the next save is a different set. A key held for the
      // component's life would present the same `(scope, key)` with a changed
      // payload the second time, and the API answers that with
      // `SYNC_IDEMPOTENCY_KEY_REUSED` — a non-retryable 409 on a screen whose
      // whole job is to be saved more than once.
      //
      // The cost of a fresh key per attempt is that a retry after a dropped
      // answer writes again rather than replaying. Harmless here and only here:
      // this route replaces the set rather than adding to it, so writing it
      // twice lands the same rows.
      const answer = await client.request(UserAssignmentsController_replace_v1, {
        path: { id: personId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: newIdempotencyKey() },
      });

      // Reconciled against the response, which describes the rows that were
      // actually written — not against `body`, which is only what was asked for.
      // The two agree today; deriving the screen's state from the request is how
      // they would stop agreeing without anybody noticing.
      //
      // Unless the response was truncated. It is bounded at the maximum the DTO
      // accepts rather than at a page, so this can only happen in a park with
      // more live outlets than that — but a partial page read as a whole set is
      // this screen's own defect in a second costume, so it re-reads instead.
      if (answer.items.length < answer.total) {
        reload();
      } else {
        setState({
          kind: 'loaded',
          rows: mergeAssignments(
            // The universe does not change under a `PUT`; only the rows do.
            state.kind === 'loaded' ? state.rows.map((row) => row.outlet) : [],
            answer.items,
          ),
        });
        setTicked(assignedOutletIds(answer.items));
      }

      setSaved(true);
    } catch (error) {
      // Nothing here is about one outlet — a lapsed session, a role that does not
      // permit it, an outlet somebody retired while this was open — so it goes
      // above the submit, where it stays until it is dealt with.
      setSaveError(messageForFailure(error));
    } finally {
      setSaving(false);
    }
  };

  const person =
    peopleState.kind === 'loaded'
      ? peopleState.people.find((each) => each.id === personId)
      : undefined;

  // The same text the items render, handed to `<Select>` so the trigger can
  // show it. It has to be built here rather than read off the rendered items:
  // Base UI resolves the label from this map, not from the DOM.
  const personItems =
    peopleState.kind === 'loaded'
      ? peopleState.people.map((each) => ({
          label: `${each.displayName} (${each.staffCode})`,
          value: each.id,
        }))
      : [];

  const rows = state.kind === 'loaded' ? state.rows : [];
  const byRole = reachesByRole(rows);
  const changed = state.kind === 'loaded' && !sameSet(ticked, assignedOutletIds(rowIds(rows)));

  return (
    <Screen
      title="Staffing"
      lede="Which outlets a person may work at"
      description="Which outlets a person may work at. An assignment is what lets somebody be named at that outlet's terminals and puts their PIN on the device's roster, so it can be checked with no network."
      aside={
        <>
          {state.kind === 'loaded' && (
            <ScreenFact label="Assigned">
              <span className="font-mono numeric">{ticked.size}</span> of{' '}
              <span className="font-mono numeric">{rows.length}</span>{' '}
              {rows.length === 1 ? 'outlet' : 'outlets'}
              {changed && ' — not saved yet'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="staffing-person" className="text-sm text-muted-foreground">
              Person
            </Label>
            <Select
              value={personId}
              // `items` is what lets the trigger read a name. Without it Base UI
              // has no value-to-label map and `<SelectValue>` falls back to the
              // raw value — which here is a user id, so the toolbar showed a UUID.
              items={personItems}
              disabled={peopleState.kind !== 'loaded'}
              onValueChange={(next) => setPersonId(next)}
            >
              <SelectTrigger id="staffing-person" aria-label="Person" className="h-9 w-full">
                <SelectValue placeholder="Choose somebody" />
              </SelectTrigger>
              <SelectContent>
                {/* The group carries the popup's padding (`p-1.5`). Items placed
                    straight into the content sit flush against its edges. */}
                <SelectGroup>
                  {peopleState.kind === 'loaded' &&
                    peopleState.people.map((each) => (
                      <SelectItem key={each.id} value={each.id}>
                        {each.displayName} ({each.staffCode})
                      </SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </>
      }
    >
      {/* Said next to the table rather than as an error, because nothing has gone
          wrong: it is FR-111 being true. Ticking a box does not widen a reach
          that is already every outlet — it does something else entirely, and an
          owner deciding whether to tick one needs to know which. */}
      {byRole && person !== undefined && (
        <p className="max-w-prose text-sm text-muted-foreground">
          {person.displayName} is {aOrAn(labelForRole(person.role))}, so they already reach every
          outlet&apos;s records in the back office whether or not anything below is ticked. Ticking
          one does not widen that. It rosters them at that outlet&apos;s{' '}
          <span className="text-foreground">terminals</span> — a person can only be named at a till
          in the outlet its lease was issued for, whatever role they hold.
        </p>
      )}

      {saved && !changed && (
        // `status` and not the component's default `alert`: a save that worked is
        // not an interruption.
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>
            {ticked.size === 0
              ? `${person?.displayName ?? 'They'} is assigned to no outlets.`
              : `${person?.displayName ?? 'They'} is assigned to ${String(ticked.size)} ${
                  ticked.size === 1 ? 'outlet' : 'outlets'
                }.`}
          </AlertDescription>
        </Alert>
      )}

      {/* `stated`, and a width on every column but `Outlet`. The sheet takes the
          whole field now, so a five-column table with one unstated width would
          share the slack back out across all five and open a gap in the middle
          of the row — which is what `docs/design-language.md`, under **Open**,
          measured against Outlets. Nothing here is an amount, so nothing needs
          the sheet's right edge, and the slack falls into the name. */}
      <ScreenTable label="Outlets" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Assigned</TableHead>
            <TableHead className="w-32">Code</TableHead>
            <TableHead>Outlet</TableHead>
            <TableHead className="w-56">Kind</TableHead>
            <TableHead className="w-44">Reach</TableHead>
          </TableRow>
        </TableHeader>

        {(peopleState.kind === 'loading' || state.kind === 'loading') && (
          <TableSkeleton columns={COLUMNS} />
        )}

        {peopleState.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            <LoadFailed
              error={peopleState.error}
              onRetry={() => setPeopleReloads((count) => count + 1)}
            />
          </TableNotice>
        )}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {peopleState.kind === 'loaded' && state.kind === 'idle' && (
          <TableNotice columns={COLUMNS}>
            {peopleState.people.length === 0 ? (
              <EmptyState
                title="Nobody to staff yet"
                description="Everybody who signs in needs a person before they can be assigned anywhere. Add them on the People screen first."
              />
            ) : (
              <EmptyState
                title="Choose somebody"
                description="Pick a person above to see the outlets they may work at, and to change them."
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (rows.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No outlets yet"
                description="Nobody can be assigned anywhere until the park has an outlet. Create the first one on the Outlets screen."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {rows.map((row) => (
                <OutletTableRow
                  key={row.outlet.id}
                  row={row}
                  ticked={ticked.has(row.outlet.id)}
                  disabled={saving}
                  onToggle={(next) =>
                    setTicked((current) => {
                      const updated = new Set(current);

                      if (next) updated.add(row.outlet.id);
                      else updated.delete(row.outlet.id);

                      return updated;
                    })
                  }
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && rows.length > 0 && (
        <div className="flex flex-wrap items-start justify-between gap-6">
          {/* The one consequence somebody hesitating over an unticked box needs,
              said before they press the button rather than discovered after. */}
          <p className="max-w-prose text-sm text-muted-foreground">
            Removing an assignment does not touch anything the person recorded while it held. Their
            sales, tickets and audit entries stay exactly where they are and stay attributed to
            them; what changes is where they may work from tomorrow.
          </p>

          <div className="space-y-3">
            {saveError !== null && (
              <Alert variant="destructive">
                <IconAlertTriangle />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}

            {/* The screen's one accent, and deliberately **not** disabled when
                nothing has changed. Replacing a set with itself is idempotent by
                nature, and a button that greys out on "no change" would hide the
                exact failure this screen was written against: a wrong prefill
                looks unchanged, so the guard would read as reassurance while the
                first tick anybody made sent the wrong set anyway. */}
            <Button
              className="w-full sm:w-auto"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Saving…' : 'Save assignments'}
            </Button>
          </div>
        </div>
      )}
    </Screen>
  );
}

function OutletTableRow({
  row,
  ticked,
  disabled,
  onToggle,
}: {
  row: AssignmentRow;
  ticked: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  // The outlet's name is the checkbox's accessible name, so a reader moving
  // through the column hears what each box is for rather than "checkbox" three
  // times. Named from the cell that is already on screen, so the two cannot
  // disagree.
  const nameId = `outlet-name-${row.outlet.id}`;

  return (
    <TableRow>
      <TableCell>
        {/* Slate when ticked, not the registry's `bg-primary`. Seven live
            outlets is seven green boxes on the one screen whose primary action
            is a green button, and chrome that spends the accent leaves nothing
            for the control doing the work — the same rule the navigation margin
            follows for its active pill. The registry component is generated and
            `shadcn add -o` overwrites it, so the override is stated here. */}
        <Checkbox
          aria-labelledby={nameId}
          checked={ticked}
          disabled={disabled}
          onCheckedChange={(next) => onToggle(next === true)}
          className="data-checked:border-foreground data-checked:bg-foreground data-checked:text-background"
        />
      </TableCell>

      {/* Read aloud and typed back in, so it keeps the mono face and tabular
          figures it has everywhere else. It was trailing the name in the cell
          beside this one and was the first of the two things this screen
          fetched and did not show. */}
      <TableCell className="font-mono text-muted-foreground numeric">{row.outlet.code}</TableCell>

      <TableCell>
        {/* A `span` and not a `Label`. Base UI's checkbox is a `button` with
            `role="checkbox"` rather than an `input`, so a `<label for>` would
            bind to nothing; `aria-labelledby` above is what names it, and this
            is the text it names. */}
        <span id={nameId} className="text-foreground">
          {row.outlet.name}
        </span>
      </TableCell>

      <TableCell>{labelForKind(row.outlet.kind)}</TableCell>

      {/* Plain muted text and not a pill. It is a fact about the row, the same
          weight as the kind beside it, and a badge would make it the loudest
          thing in a table whose one loud thing is meant to be the toggle. */}
      <TableCell className="text-muted-foreground">{labelForVia(row.via)}</TableCell>
    </TableRow>
  );
}

/** The ids and their assignment state, in the shape `assignedOutletIds` asks for. */
const rowIds = (rows: readonly AssignmentRow[]) =>
  rows.map((row) => ({ id: row.outlet.id, assigned: row.assigned }));

/** Whether the ticked set still matches what the server last said. */
function sameSet(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) return false;

  for (const value of left) if (!right.has(value)) return false;

  return true;
}

/**
 * "an owner", "a manager".
 *
 * Four role labels, two of which begin with a vowel, and the sentence they sit in
 * is the one explaining why an owner's boxes are empty — a sentence that has to
 * read as prose or nobody finishes it.
 */
const aOrAn = (label: string): string =>
  `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label.toLowerCase()}`;
